import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadTurkishFont, TURKISH_FONT } from '../../../shared/utils/pdfTurkishFont';

const COLORS = {
  title: [15, 23, 53],
  subtitle: [73, 91, 130],
  headerBg: [27, 45, 92],
  headerText: [255, 255, 255],
  rowEven: [247, 250, 255],
  rowOdd: [235, 241, 255],
  border: [197, 210, 234],
  bodyText: [33, 42, 66]
};

const fmt = (value) => {
  const n = Number(value);
  return Number.isNaN(n)
    ? '-'
    : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch (_error) {
    return '-';
  }
};

const toText = (value) => (value === null || value === undefined ? '-' : String(value));

export async function exportStressTestPdf({
  scenarioName,
  companyName,
  periodLabel,
  generatedAt,
  snapshot,
  aiReportText
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await loadTurkishFont(doc);
  doc.setFont(TURKISH_FONT, 'normal');

  const margin = 12;
  let y = 14;

  doc.setTextColor(...COLORS.title);
  doc.setFont(TURKISH_FONT, 'bold');
  doc.setFontSize(16);
  doc.text('ScenarioLab - Finansal Etki Simulasyonu', margin, y);
  y += 6;

  doc.setTextColor(...COLORS.subtitle);
  doc.setFont(TURKISH_FONT, 'normal');
  doc.setFontSize(10);
  doc.text(`Senaryo: ${toText(scenarioName)} | Sirket: ${toText(companyName)} | Donem: ${toText(periodLabel)}`, margin, y);
  y += 4.5;
  doc.text(`Rapor Tarihi: ${formatDate(generatedAt)}`, margin, y);
  y += 6;

  const base = snapshot?.baseFinancials || {};
  const scn = snapshot?.scenarioFinancials || {};
  const kpi = snapshot?.kpi || {};
  const attribution = snapshot?.attribution?.items || [];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Yonetici Ozeti KPI', 'Baz', 'Senaryo', 'Fark']],
    body: [
      ['Net Kar', fmt(kpi?.base?.netProfit), fmt(kpi?.scenario?.netProfit), fmt((Number(kpi?.scenario?.netProfit || 0) - Number(kpi?.base?.netProfit || 0)))],
      ['EBIT', fmt(kpi?.base?.ebit), fmt(kpi?.scenario?.ebit), fmt((Number(kpi?.scenario?.ebit || 0) - Number(kpi?.base?.ebit || 0)))],
      ['Brut Marj (%)', `${fmt((Number(kpi?.base?.grossMargin || 0) * 100))}%`, `${fmt((Number(kpi?.scenario?.grossMargin || 0) * 100))}%`, `${fmt((Number((kpi?.scenario?.grossMargin || 0) - (kpi?.base?.grossMargin || 0)) * 100))}%`],
      ['Faiz Karsilama', fmt(kpi?.base?.interestCoverage), fmt(kpi?.scenario?.interestCoverage), fmt((Number(kpi?.scenario?.interestCoverage || 0) - Number(kpi?.base?.interestCoverage || 0)))],
      ['Cari Oran', fmt(kpi?.base?.currentRatio), fmt(kpi?.scenario?.currentRatio), fmt((Number(kpi?.scenario?.currentRatio || 0) - Number(kpi?.base?.currentRatio || 0)))],
      ['Net Borc', fmt(kpi?.base?.netDebt), fmt(kpi?.scenario?.netDebt), fmt((Number(kpi?.scenario?.netDebt || 0) - Number(kpi?.base?.netDebt || 0)))]
    ],
    theme: 'grid',
    styles: {
      font: TURKISH_FONT,
      fontSize: 9,
      textColor: COLORS.bodyText,
      lineColor: COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: COLORS.headerBg,
      textColor: COLORS.headerText,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: COLORS.rowEven
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.row.index % 2 === 1) {
        data.cell.styles.fillColor = COLORS.rowOdd;
      }
    }
  });

  y = doc.lastAutoTable.finalY + 6;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Pro-forma Finansal Ozeti', 'Baz', 'Senaryo', 'Fark']],
    body: ['revenue', 'cogs', 'opex', 'interestCost', 'fxDifference', 'ebit', 'netProfit', 'currentAssets', 'currentLiabilities', 'netDebt']
      .map((key) => [key, fmt(base[key]), fmt(scn[key]), fmt((Number(scn[key] || 0) - Number(base[key] || 0)))]),
    theme: 'grid',
    styles: {
      font: TURKISH_FONT,
      fontSize: 8.5,
      textColor: COLORS.bodyText,
      lineColor: COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: COLORS.headerBg,
      textColor: COLORS.headerText,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: COLORS.rowEven
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.row.index % 2 === 1) {
        data.cell.styles.fillColor = COLORS.rowOdd;
      }
    }
  });

  y = doc.lastAutoTable.finalY + 6;
  if (y > 248) {
    doc.addPage();
    y = 14;
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Attribution', 'Etki (TL)', 'Etki (%)']],
    body: attribution.length === 0
      ? [['Veri yok', '-', '-']]
      : attribution.map((item) => [
          toText(item.driver),
          fmt(item.impactTl),
          `${fmt(Number(item.impactPct || 0) * 100)}%`
        ]),
    theme: 'grid',
    styles: {
      font: TURKISH_FONT,
      fontSize: 9,
      textColor: COLORS.bodyText,
      lineColor: COLORS.border,
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: COLORS.headerBg,
      textColor: COLORS.headerText,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: COLORS.rowEven
    }
  });

  y = doc.lastAutoTable.finalY + 8;
  if (y > 240) {
    doc.addPage();
    y = 14;
  }

  doc.setFont(TURKISH_FONT, 'bold');
  doc.setTextColor(...COLORS.title);
  doc.setFontSize(12);
  doc.text('AI Raporu', margin, y);
  y += 4.5;
  doc.setFont(TURKISH_FONT, 'normal');
  doc.setTextColor(...COLORS.bodyText);
  doc.setFontSize(9.5);

  const aiText = toText(aiReportText).slice(0, 5000);
  const wrapped = doc.splitTextToSize(aiText, 186);
  doc.text(wrapped, margin, y);

  const filename = `scenario_lab_${(scenarioName || 'rapor').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

